import type { SchoolTestDomain } from '@mathflat/domain/@entities/(Content)/SchoolTest/domain'
import { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { LEVEL_OF_ACHIEVEMENT_COUNT_DICT } from '@mathflat/domain/@entities/ConceptChip/constants'
import type { ConceptChipDomain } from '@mathflat/domain/@entities/ConceptChip/domain'
import type { Entity as ConceptChipEntity } from '@mathflat/domain/@entities/ConceptChip/dto'
import { CURRICULUM_KEYS } from '@mathflat/domain/@entities/Curriculum/constant'
import type { Semester } from '@mathflat/domain/@entities/SchoolSystem/schema'
import { flatMap } from 'lodash-es'
import { minBy } from 'lodash-es'
import { action, makeAutoObservable, makeObservable, observable, runInAction, toJS } from 'mobx'

import { TOAST_STR } from '@/constant'
import { EMPTY_NUMBER_ID, MAX_REQUEST_NUMBER } from '@/constant/common'
import { _workbookController } from '@/controller/_workbook.controller'
import { achievementAnalysisController } from '@/controller/achievementAnalysis.controller'
import { aiController } from '@/controller/ai.controller.ts'
import { smsController } from '@/controller/sms.controller.ts'
import { DTO, SmsPost, type Student, WorkbookColl, Worksheet } from '@/dto'
import type {
  AchievementReport,
  AchievementReportColl,
} from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import { CheckableMap } from '@/models/CheckableMap'
import commonRepo from '@/repository/common.repo'
import { type CoreItem, curriculumCoreService } from '@/service/curriculumCore.service'
import modalService from '@/service/modal.service'
import WebviewService from '@/service/webview.service.ts'
import appPostMsg from '@/utils/appPostMsg.ts'
import { remoteStorageService } from '@/utils/axios/remoteStorage'
import { urlPreview } from '@/utils/download'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import type { EventPropertiesSchema } from '@/utils/trackings/schema/eventProperties.schema'

import {
  SchoolTestFilterManager,
  type SchoolTestFilterOptionType,
} from './_SchoolTestFilterManager.service'
import { AchievementConceptChipManager } from './AchievementConceptChipManager.service'

type UIFilterT = {
  recommended: boolean
  achievementLevelSet: Set<ConceptChipDomain.LevelOfAchievement>
}

const createDefaultUIFilter = (): UIFilterT => ({
  recommended: false,
  achievementLevelSet: new Set<ConceptChipDomain.LevelOfAchievement>(),
})

export type BaseExternalData = {
  __version: `v${number}`
  __timestamp: number
}

export type AnalysisAchievementExternal =
  | AnalysisAchievementExternalV1
  | AnalysisAchievementExternalV2
  | AnalysisAchievementExternalV3

export type AnalysisAchievementExternalV1 = Record<
  res.Student['id'],
  {
    grade: CoreItem['gradeSubject']
    schoolType: CoreItem['schoolType']
    semester: CoreItem['semester']
  }
>

export type AnalysisAchievementExternalV2 = Record<
  res.Student['id'],
  {
    schoolType: common.SchoolType
    grade: common.GradeSubject
    semester: Semester
    workbookFilterOptionsByCurriculumKey?: {
      [key: string]: {
        workbooks: WorkbookEntity.SupportedWorkbook[]
      }
    }
    schoolTestFilterOptionsByCurriculumKey?: SchoolTestFilterOptionsByCurriculumKey
  }
>

export type AnalysisAchievementExternalV3 = Record<
  res.Student['id'],
  {
    schoolType: common.SchoolType
    grade: common.GradeSubject
    semester: Semester
    revision: common.Revision
    workbookFilterOptionsByCurriculumKey?: WorkbookFilterOptionsByCurriculumKey
    schoolTestFilterOptionsByCurriculumKey?: SchoolTestFilterOptionsByCurriculumKey
    useRecommendedFilter?: boolean
  }
> &
  Record<'__version', `v${number}`>

type WorkbookFilterOptionsByCurriculumKey = {
  [key: string]: {
    workbooks: Pick<WorkbookEntity.SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>[]
  }
}

export type SchoolTestDataForService = Pick<
  SchoolTestDomain.SchoolTestOrigin,
  'id' | 'title' | 'school'
>

type SchoolTestFilterOptionsByCurriculumKey = {
  [key: string]: {
    schoolTests: SchoolTestDataForService[]
    schoolTestFilterOption: SchoolTestFilterOptionType
  }
}

export class AchievementAnalysisService {
  static REMOTE_STORAGE_VERSION = 3
  achievementConceptChipManager = new AchievementConceptChipManager() // 유형칩 관리 서비스
  schoolSearchFilterManager = new SchoolTestFilterManager() // 학교별 기출필터 서비스
  uiStore = new UIStore()

  selectedStudentId?: string

  selectedChipId = EMPTY_NUMBER_ID // 선택된 유형칩
  selectedConceptChipDetail: ConceptChipEntity.GroupAchievementConceptChipDetail | null = null
  studyLogMap = new Map<
    ConceptChipEntity.AchievementConceptChip['conceptChipId'],
    res.AchievementStudyLog[]
  >() // 선택된 유형칩의 최근 학습 이력
  checkedConceptChipIdSet = new Set<ConceptChipEntity.AchievementConceptChip['id']>() // 체크된 유형칩 id Set

  workbookResponseFilterColls: {
    [key in WorkbookEntity.SupportedWorkbookType]:
      | WorkbookColl<WorkbookEntity.WorkbookResponse<key>>
      | undefined
  } = {
    CUSTOM_SIGNATURE: undefined,
    ACADEMY_GROUP: undefined,
    PUBLIC: undefined,
    SCHOOL: undefined,
  } // 필터 - 시그니처교재, 학원그룹교재, 시중교재, 교과서(리모트스토리이지에서 가져온 값)

  reportPdfInfo?: { pdf: { minPage: number; maxPage: number } } // 유형분석 보고서 정보
  pdfClient: { connected: boolean } = { connected: false } // pdf 클라이언트 연결 여부
  achievementReportColl: AchievementReportColl | null = null // 유형분석 보고서 목록
  selectedLessonClassChallenge: res.LessonClassChallengeInfo | null = null

  constructor() {
    makeAutoObservable(this)
  }

  unselectChipId = () => {
    this.selectedChipId = EMPTY_NUMBER_ID
  }

  static koBookType = (
    bookType: res.AchievementStudyLog['type'],
    subType: res.AchievementStudyLog['subType'],
  ) => {
    if (bookType === 'WORKBOOK') return '교재'
    if (bookType === 'WORKSHEET') {
      // 학습지 내에 "챌린지" / "자기주도학습"이 포함됨.
      return Worksheet.subTypeKr(subType, {
        availableSubTypes: [12, 13, 14, 15, 16],
        fallbackText: '학습지',
      })
    }

    return ''
  }

  private achievementFilter(achievementConceptChip: ConceptChipEntity.AchievementConceptChip) {
    const achievementLevels = [...this.uiStore.filter.achievementLevelSet.values()]

    if (achievementLevels.length === 0) return true

    return (
      achievementConceptChip.achievementLevel &&
      achievementLevels.includes(achievementConceptChip.achievementLevel)
    )
  }

  private requiredFilter(achievementConceptChip: ConceptChipEntity.AchievementConceptChip) {
    if (!this.uiStore.filter.recommended) return true

    return achievementConceptChip.recommended
  }

  get isFunctionDisabled() {
    return {
      checkAchievementChip: !!this.achievementConceptChipManager.filter.requestDate,
    }
  }

  get filteredAchievementConceptChips() {
    return this.achievementConceptChipManager.achievementConceptChips.filter(
      (achievementConceptChip) =>
        this.achievementFilter(achievementConceptChip) &&
        this.requiredFilter(achievementConceptChip),
    )
  }

  get requiredFilteredAchievementConceptChips() {
    return this.achievementConceptChipManager.achievementConceptChips.filter(
      (achievementConceptChip) => this.requiredFilter(achievementConceptChip),
    )
  }

  get checkedFilteredAchievementConceptChips() {
    return this.filteredAchievementConceptChips.filter((chip) =>
      this.checkedConceptChipIdSet.has(chip.id),
    )
  }

  get checkedFilteredCountByAchievementLevel() {
    const result = this.checkedFilteredAchievementConceptChips.reduce(
      (prev, { achievementLevel }) => {
        if (!achievementLevel) return prev

        return {
          ...prev,
          [achievementLevel]: prev[achievementLevel] + 1,
        }
      },
      LEVEL_OF_ACHIEVEMENT_COUNT_DICT,
    )

    // convert for data tracking
    const data = {} as Record<Lowercase<ConceptChipDomain.LevelOfAchievement>, number>
    Object.entries(result).forEach(([k, v]) => {
      data[k.toLowerCase()] = v
    })

    return data
  }

  get countByAchievementLevel(): Record<ConceptChipDomain.LevelOfAchievement, number> {
    return this.achievementConceptChipManager.achievementConceptChips
      .filter((v) => this.requiredFilter(toJS(v)))
      .reduce((prev, { achievementLevel }) => {
        if (!achievementLevel) return prev

        return {
          ...prev,
          [achievementLevel]: prev[achievementLevel] + 1,
        }
      }, LEVEL_OF_ACHIEVEMENT_COUNT_DICT)
  }

  get prevCurNextDetailInfo() {
    const targetIndex = this.checkedFilteredAchievementConceptChips.findIndex(
      ({ conceptChipId }) => conceptChipId === this.selectedChipId,
    )

    const getDetailInfo = (index) => {
      const achievementConceptChip = this.checkedFilteredAchievementConceptChips[index]

      if (!achievementConceptChip) {
        return null
      }

      const studyLogs = this.studyLogMap.get(achievementConceptChip.conceptChipId)

      return { achievementConceptChip, studyLogs }
    }

    const prev = getDetailInfo(targetIndex - 1)
    const current = getDetailInfo(targetIndex)
    const next = getDetailInfo(targetIndex + 1)

    return {
      prev,
      current,
      next,
    }
  }

  get gradeSemester() {
    return (
      curriculumCoreService.getItemByTrieKey(
        this.achievementConceptChipManager.filter.curriculumKey,
      )?.labelShort ?? '중1-1'
    )
  }

  get pdfRenderData() {
    return {
      ...this.reportPdfInfo,
    }
  }

  nextConceptChip() {
    if (this.prevCurNextDetailInfo.next) {
      this.selectedChipId = this.prevCurNextDetailInfo.next.achievementConceptChip.conceptChipId
    }
  }

  prevConceptChip() {
    if (this.prevCurNextDetailInfo.prev) {
      this.selectedChipId = this.prevCurNextDetailInfo.prev.achievementConceptChip.conceptChipId
    }
  }

  updateSelectedChipId() {
    this.selectedChipId =
      this.checkedFilteredAchievementConceptChips.sort(
        (x, y) => x.orderingNumber - y.orderingNumber,
      )[0]?.id ?? EMPTY_NUMBER_ID
  }

  async fetchAchievementStudyLogs(studentId: res.Student['id']) {
    if (
      !this.prevCurNextDetailInfo.current ||
      this.studyLogMap.get(this.prevCurNextDetailInfo.current.achievementConceptChip.conceptChipId)
    )
      return

    const conceptChipId = this.prevCurNextDetailInfo.current.achievementConceptChip.conceptChipId
    const params = this.achievementConceptChipManager.filter.requestDate
      ? { requestDate: this.achievementConceptChipManager.filter.requestDate }
      : undefined

    const data = await achievementAnalysisController.getAchievementStudyLogs(
      studentId,
      conceptChipId,
      params,
    )

    this.studyLogMap.set(conceptChipId, data)

    return data
  }

  isChecked(conceptChipId: ConceptChipEntity.AchievementConceptChip['id']) {
    return this.checkedConceptChipIdSet.has(conceptChipId)
  }

  toggleCheck(ids: ConceptChipEntity.AchievementConceptChip['id'][]) {
    const status = this.getCheckedStatus(ids)
    let targetId: undefined | number

    if (status === 'EVERY') {
      ids.forEach((id) => this.checkedConceptChipIdSet.delete(id))

      // 선택한 그룹이 모두 취소되므로 전체 칩 중에서 새로 선택 id를 뽑음
      targetId = minBy(
        this.filteredAchievementConceptChips.filter(({ id }) =>
          [...this.checkedConceptChipIdSet.values()].includes(id),
        ),
        'orderingNumber',
      )?.id
    } else {
      ids.forEach((id) => this.checkedConceptChipIdSet.add(id))

      // 선택한 그룹 내에서 선택 id를 뽑음
      targetId = minBy(
        this.filteredAchievementConceptChips.filter(({ id }) => ids.includes(id)),
        'orderingNumber',
      )?.id

      _mixpanelUtils.uiTrack('유형분석_상세정보_API', {
        ...this.checkedFilteredCountByAchievementLevel,
        '선택한 유형칩 수': this.checkedFilteredAchievementConceptChips.length,
      })
    }

    this.selectedChipId = targetId ?? EMPTY_NUMBER_ID
  }

  getCheckedStatus(ids: ConceptChipEntity.AchievementConceptChip['id'][]) {
    const checkedTargetIds = [...this.checkedConceptChipIdSet.values()].filter((id) =>
      ids.includes(id),
    )

    if (ids.length === checkedTargetIds.length) {
      return 'EVERY'
    }
    if (checkedTargetIds.length === 0) {
      return 'EMPTY'
    }
    return 'SOME'
  }

  getWrongProblemCountOfCheckedFilteredConceptChips() {
    const checkedFilteredIds = this.checkedFilteredAchievementConceptChips.map(({ id }) => id)
    const studyLogsOfCheckedConceptChip = checkedFilteredIds.map((id) => this.studyLogMap.get(id))

    return studyLogsOfCheckedConceptChip.reduce(
      (prev, cur) => prev + (cur?.filter(({ result }) => result !== 'CORRECT').length ?? 0),
      0,
    )
  }

  /* 기본 필터 관련 로직 */
  loadStoredFilterAndFetchConcepChips(
    storageKey: string,
    { isFetchingConceptChips }: { isFetchingConceptChips?: boolean } = {
      isFetchingConceptChips: true,
    },
  ) {
    const storedInfo = remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]

    const recommended = storedInfo?.useRecommendedFilter ?? false
    this.uiStore.filter.recommended = recommended

    const workbooks =
      storedInfo?.workbookFilterOptionsByCurriculumKey?.[
        this.achievementConceptChipManager.filter.curriculumKey
      ]?.workbooks ?? []

    this.updateFilterWorkbook(
      'CUSTOM_SIGNATURE',
      workbooks.filter((wb) => wb.type === 'CUSTOM_SIGNATURE'),
    )
    this.updateFilterWorkbook(
      'ACADEMY_GROUP',
      workbooks.filter((wb) => wb.type === 'ACADEMY_GROUP'),
    )
    this.updateFilterWorkbook(
      'PUBLIC',
      workbooks.filter((wb) => wb.type === 'PUBLIC'),
    )
    this.updateFilterWorkbook(
      'SCHOOL',
      workbooks.filter((wb) => wb.type === 'SCHOOL'),
    )

    if (!commonRepo.isSchoolflat) {
      const schoolTests =
        storedInfo?.schoolTestFilterOptionsByCurriculumKey?.[
          this.achievementConceptChipManager.filter.curriculumKey
        ]?.schoolTests ?? []
      const schoolTestFilterOption =
        storedInfo?.schoolTestFilterOptionsByCurriculumKey?.[
          this.achievementConceptChipManager.filter.curriculumKey
        ]?.schoolTestFilterOption ?? undefined

      this.updateFilterSchoolTest(schoolTests, schoolTestFilterOption)
    }

    if (isFetchingConceptChips) {
      this.fetchConceptChipsAndMakeChips()
    }
  }

  // 교재 필터 데이터를 반환하는 helper 함수
  private getWorkbooksFilterData(storageKey: string) {
    if (!storageKey) return {}

    const newWorkbooks = flatMap(this.achievementConceptChipManager.filter.workbookList)
    const newSet = new Set<
      Pick<WorkbookEntity.SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>
    >(newWorkbooks)

    return {
      workbookFilterOptionsByCurriculumKey: {
        ...remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
          ?.workbookFilterOptionsByCurriculumKey,
        [this.achievementConceptChipManager.filter.curriculumKey]: {
          workbooks: [...newSet.values()].map((workbook) => ({
            id: workbook.id,
            type: workbook.type,
            fulltitle: workbook.fulltitle,
            publisher: workbook.publisher,
          })),
        },
      },
    }
  }

  // 학교 시험 필터 데이터를 반환하는 helper 함수
  private getSchoolTestFilterData(storageKey: string) {
    if (commonRepo.isSchoolflat) {
      return {}
    }

    if (!storageKey) return {}

    return {
      schoolTestFilterOptionsByCurriculumKey: {
        ...remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
          ?.schoolTestFilterOptionsByCurriculumKey,
        [this.achievementConceptChipManager.filter.curriculumKey]: {
          schoolTests: this.achievementConceptChipManager.filter.schoolTests,
          schoolTestFilterOption: this.schoolSearchFilterManager.schoolTestFilterOption,
        },
      },
    }
  }

  // 기본 커리큘럼 정보를 반환하는 helper 함수
  private getBasicCurriculumData() {
    const curriculumInfo = curriculumCoreService.getItemByTrieKey(
      this.achievementConceptChipManager.filter.curriculumKey,
    )

    return curriculumInfo
      ? {
          schoolType: curriculumInfo.schoolType,
          grade: curriculumInfo.gradeSubject,
          semester: curriculumInfo.semester,
          useRecommendedFilter: this.uiStore.filter.recommended,
        }
      : { useRecommendedFilter: this.uiStore.filter.recommended }
  }

  // 통합 필터 저장 함수
  async saveFilterToRemoteStorage(storageKey: string) {
    if (!storageKey) {
      return
    }

    const prevData = remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
    const basicData = this.getBasicCurriculumData()
    const workbooksData = this.getWorkbooksFilterData(storageKey)
    const schoolTestData = this.getSchoolTestFilterData(storageKey)

    const mergedData: AnalysisAchievementExternalV3 = {
      [storageKey]: {
        ...prevData,
        ...basicData,
        ...workbooksData,
        ...schoolTestData,
      },
      __version: `v${AchievementAnalysisService.REMOTE_STORAGE_VERSION}`,
    } as AnalysisAchievementExternalV3

    await remoteStorageService.patch('PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석', mergedData)
  }

  updateFilterWorkbook(
    type: keyof AchievementConceptChipManager['filter']['workbookList'],
    workbooks: Pick<WorkbookEntity.SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>[],
  ) {
    this.achievementConceptChipManager.updateFilter({
      workbookList: {
        ...this.achievementConceptChipManager.filter.workbookList,
        [type]: workbooks,
      },
    })
    this.updateSelectedChipId()
  }

  updateFilterSchoolTest(
    schoolTests: SchoolTestDataForService[],
    schoolTestFilterOption?: Partial<SchoolTestFilterOptionType>,
  ) {
    if (commonRepo.isSchoolflat) return
    // 유형분석 조회 필터 업데이트
    this.achievementConceptChipManager.updateFilter({
      schoolTests: schoolTests,
    })
    // 기출문제조회 조건 필터 업데이트(유형분석조회에 사용하는 교재id가 아니라 교재id를 받아오기 위한 필터로 로컬이나 리모트스토리지에서 관리)
    this.schoolSearchFilterManager.updateInitialSchoolTestFilter(schoolTestFilterOption)
    this.updateSelectedChipId()
  }

  initSchoolTestFilterOptions() {
    if (commonRepo.isSchoolflat) return
    try {
      const options = this.schoolSearchFilterManager.schoolTestFilterOption
      this.schoolSearchFilterManager.setYearOptions(options.year)
      this.schoolSearchFilterManager.setTestTypeOptions(options.schoolTestType)
      this.schoolSearchFilterManager.setSchoolCodes([...options.schoolChecked.values()])
    } catch (err) {
      commonErrorMessage(err)
      throw err
    }
  }

  resetAll() {
    this.uiStore.resetFilter()
    this.unselectChipId()
    this.resetCheckedConceptChipIdSet()
    this.schoolSearchFilterManager.resetAllFilter()
    this.schoolSearchFilterManager.resetSchoolTestFilterOption()
    this.achievementConceptChipManager.resetFilter()
  }

  resetCheckedConceptChipIdSet() {
    this.checkedConceptChipIdSet.clear()
  }

  resetFilterExceptCurriculumKey() {
    this.achievementConceptChipManager.updateFilter({
      requestDate: null,
      workbookList: { CUSTOM_SIGNATURE: [], ACADEMY_GROUP: [], PUBLIC: [], SCHOOL: [] },
      schoolTests: [],
      curriculumKey: this.achievementConceptChipManager.filter.curriculumKey,
    })
    this.schoolSearchFilterManager.resetSchoolTestFilterOption()
  }

  /* --- 교재 팝오버 관련 로직 --- */
  async fetchWorkbookFilterColls() {
    try {
      this.uiStore.loadingWorkbookPopover = true
      const curriculumKey = this.achievementConceptChipManager.filter.curriculumKey

      // 교과서의 경우 특정 학년에서 curriculumKey를 그대로 내려보내면 안되기에 mapping하는 로직
      // 특정 학년의 교과서는 2학기를 더 이상 지원하지 않기 때문. (1개의 교과서를 1년동안 사용)
      const curriculumKeyForSchool = (() => {
        const curriculumInfo = curriculumCoreService.getItemByTrieKey(curriculumKey)
        if (!curriculumInfo) return curriculumKey

        const { schoolType, trieKey } = curriculumInfo

        // 중학교 교과서는 학기 구분이 없기 때문에 학기 curriculumKey가 아닌 학년 curriculumKey를 보내야 함
        if (schoolType === 'MIDDLE') {
          return curriculumInfo.upperTrieKey
        }

        // 고등수학(하)의 경우 curriculumKey를 고등수학(상)의 curriculumKey를 보내야 함
        if (schoolType === 'HIGH' && trieKey === CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_하) {
          return CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_수학_상
        }

        return curriculumKey
      })()
      const [signatureRes, academyGroupRes, publicRes, schoolRes] = await Promise.all([
        _workbookController.fetchWorkbooks<WorkbookDomain.TYPE['커스텀시그니처교재']>({
          type: WorkbookDomain.TYPE.커스텀시그니처교재,
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
          featureTypes: commonRepo.permission.bySubscription.workbook.signature.featureTypesParams,
          signatureTypes:
            commonRepo.permission.bySubscription.workbook.signature
              .enableAssignSignatureTypesParams,
        }),
        _workbookController.fetchWorkbooks<WorkbookDomain.TYPE['학원그룹교재']>({
          type: WorkbookDomain.TYPE.학원그룹교재,
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
        }),
        _workbookController.fetchWorkbooks<WorkbookDomain.TYPE['시중교재']>({
          type: WorkbookDomain.TYPE.시중교재,
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
        }),

        _workbookController.fetchWorkbooks<WorkbookDomain.TYPE['교과서']>({
          type: WorkbookDomain.TYPE.교과서,
          curriculumKey: curriculumKeyForSchool,
          size: MAX_REQUEST_NUMBER,
        }),
      ])

      this.workbookResponseFilterColls = {
        CUSTOM_SIGNATURE: new WorkbookColl(
          CheckableMap.fromArr(signatureRes.workbookReponses, 'id'),
        ),
        ACADEMY_GROUP: new WorkbookColl(
          CheckableMap.fromArr(academyGroupRes.workbookReponses, 'id'),
        ),
        PUBLIC: new WorkbookColl(CheckableMap.fromArr(publicRes.workbookReponses, 'id')),
        SCHOOL: new WorkbookColl(CheckableMap.fromArr(schoolRes.workbookReponses, 'id')),
      }

      this.uiStore.loadingWorkbookPopover = false
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  updateCurriculumKeyByStudentGrade(
    ...args: Parameters<typeof curriculumCoreService.getDefaultTrieKeyBySchoolGrade>
  ) {
    const targetGradeSemesterCurriculumKey = curriculumCoreService.getDefaultTrieKeyBySchoolGrade(
      ...args,
    )

    if (targetGradeSemesterCurriculumKey) {
      this.achievementConceptChipManager.updateFilter({
        curriculumKey: targetGradeSemesterCurriculumKey,
      })

      this.fetchWorkbookFilterColls()
    }
  }

  updateCurriculumKeyByGradeSemester(
    ...args: Parameters<typeof curriculumCoreService.getItemByGradeSemesterInfo>
  ) {
    const targetGradeSemesterCurriculumKey = curriculumCoreService.getItemByGradeSemesterInfo(
      ...args,
    )?.trieKey

    if (targetGradeSemesterCurriculumKey) {
      this.achievementConceptChipManager.updateFilter({
        curriculumKey: targetGradeSemesterCurriculumKey,
      })

      this.fetchWorkbookFilterColls()
    }
  }

  /**
   * 그룹 유형분석 conceptChip fetch
   */
  async fetchGroupConceptChips(
    ...args: Parameters<AchievementConceptChipManager['fetchGroupConceptChips']>
  ) {
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchGroupConceptChips(...args),
    )
  }

  /**
   * 그룹 유형분석 conceptChip fetch
   */
  async fetchGroupPastAchievementsAndMakeChips(
    ...args: Parameters<AchievementConceptChipManager['fetchGroupPastAchievements']>
  ) {
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchGroupPastAchievements(...args),
    )
  }

  async fetchGroupConceptChipDetail(
    ...args: Parameters<(typeof achievementAnalysisController)['getGroupConceptChipDetail']>
  ) {
    const chipDetail = await achievementAnalysisController.getGroupConceptChipDetail(...args)
    runInAction(() => {
      this.selectedConceptChipDetail = chipDetail
    })
  }

  /**
   * 유형칩, 성취도 fetch 후 유형칩 만들기 (학년,학기 드롭다운 변경시)
   */
  async fetchAllAndMakeChips(studentId: Student['id']) {
    this.resetCheckedConceptChipIdSet()
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchAllAndMakeChips(studentId),
    )
  }

  /**
   * 유형칩 fetch 후 유형칩 만들기 (교재 및 학교별 기출 변경시)
   *  */
  async fetchConceptChipsAndMakeChips() {
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchConceptChipAndMakeChips(),
    )
  }
  /**
   * 성취도 fetch 후 유형칩 만들기 (과거 학습 이력 변경시)
   *  */
  async fetchAchievementsAndMakeChips(studentId: Student['id']) {
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchAchievementAndMakeChips(studentId),
    )
  }

  async createUpdateReport(studentId: res.Student['id'], payload: req.AchievementReport) {
    return await achievementAnalysisController.createReport(studentId, payload)
  }

  async fetchAchievementReport(studentId: res.Student['id']) {
    const achievementReportColl =
      await achievementAnalysisController.getAchievementReport(studentId)

    runInAction(() => {
      this.achievementReportColl = achievementReportColl
    })
  }

  sendSms = async (htmlUrl: string) => {
    const content = `[${commonRepo.academy?.name}]\n{학생 이름} 학생의 유형별 성취도 리포트를 보내드립니다.\n아래 URL을 눌러 바로 확인할 수 있습니다.\n감사합니다.\n보고서 확인 : ${htmlUrl}`

    const params = new SmsPost({
      sendingTarget: 'PARENTS',
      studentIdList: [this.selectedStudentId],
      content,
    })
    return smsController.sendSms(params)
  }

  deleteReports = async (reportIds: AchievementReport['id'][]) => {
    return await achievementAnalysisController.deleteReports(reportIds)
  }

  setCheckAll() {
    if (!this.achievementReportColl) return
    this.achievementReportColl.allCheck()
  }

  setCheckAllFailedReports() {
    if (!this.achievementReportColl) return
    this.achievementReportColl.toArr.forEach((report) => {
      if (report.status === 'FAIL') {
        this.achievementReportColl!.setCheck(report.id)
      }
    })
  }

  preview = async (reportId: AchievementReport['id']) => {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewAchievementReport(reportId)
        return
      }

      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await achievementAnalysisController.preview({ reportId })
        return { url: presignedUrl, fullFileName: fileName }
      }

      urlPreview(getPreviewUrl)
    } catch (e) {
      Number(e.status) === 400
        ? toastError(TOAST_STR.reportNotFoundForPreview)
        : commonErrorMessage(e)
    }
  }

  isDuplicateAchievementReport(title: string) {
    const duplicationAchievementReport = this.achievementReportColl?.toArr.find(
      (report) => report.title === title,
    )

    return !!duplicationAchievementReport
  }

  getSessionId = async () => {
    return await aiController.getSessionId()
  }

  createAiComment = async (
    sessionId: string,
    studentId: Student['id'],
    payload: req.CreateAiComment,
  ) => {
    return await aiController.createAiComment(sessionId, studentId, payload)
  }

  /**
   * 반 학생들 소단원 챌린지 설정, 조회, 해제
   *  */
  postLessonClassChallenges = async (params: req.LessonClassChallenge) => {
    try {
      const challengeSetting = await achievementAnalysisController.postLessonClassChallenges(params)
      runInAction(() => {
        this.selectedLessonClassChallenge = {
          id: challengeSetting.id,
          relationId: challengeSetting.curriculumId,
          relationType: 'LITTLE_CHAPTER',
        }
      })
      toastSuccess('해당 소단원이 전체 학생의 수업으로\n설정되었습니다.')
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  fetchLessonClassChallenges = async (lessonClassId: req.LessonClassChallenge['lessonClassId']) => {
    try {
      const { data } = await achievementAnalysisController.getLessonClassChallenges(lessonClassId)

      // 서버에서 확장 가능성을 위해 리스트 형태로 내려주고 있음. 현재는 하나의 값만 사용하기 때문에 0번째 인덱스 값을 사용
      const selectedLessonClassChallenge = data.challengeSettings[0] ?? null
      runInAction(() => {
        this.selectedLessonClassChallenge = selectedLessonClassChallenge
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  updateStudentChallengeStatus = async (challengeId: res.LessonClassChallengeInfo['id']) => {
    try {
      await achievementAnalysisController.patchLessonClassChallengeStatus({
        id: challengeId,
        status: 'INACTIVE',
      })
      if (this.selectedLessonClassChallenge?.id === challengeId) {
        runInAction(() => {
          this.selectedLessonClassChallenge = null
        })
      }
      toastSuccess('해당 소단원이 전체 학생에게\n초기화 되었습니다.')
      modalService.closeModal()
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  ////// 이벤트 트래킹
  getAchievementAnalysisDataTrackingInfo():
    | Pick<
        EventPropertiesSchema.유형분석조회,
        | '학년학기'
        | '시그니처교재'
        | '학원그룹교재'
        | '시중교재'
        | '교과서'
        | '기출연도'
        | '시험범위'
        | '추천유형만보기'
      >
    | undefined {
    try {
      const curriculumInfo = curriculumCoreService.getItemByTrieKey(
        this.achievementConceptChipManager.filter.curriculumKey,
      )

      if (!curriculumInfo) {
        return
      }

      return {
        학년학기: curriculumInfo?.labelShort ?? '',
        시그니처교재: this.achievementConceptChipManager.filter.workbookList.CUSTOM_SIGNATURE.map(
          (wb) => wb.fulltitle,
        ),
        학원그룹교재: this.achievementConceptChipManager.filter.workbookList.ACADEMY_GROUP.map(
          (wb) => wb.fulltitle,
        ),
        시중교재: this.achievementConceptChipManager.filter.workbookList.PUBLIC.map(
          (wb) => wb.fulltitle,
        ),
        교과서: this.achievementConceptChipManager.filter.workbookList.SCHOOL.map(
          (wb) => wb.fulltitle,
        ),
        기출연도: this.schoolSearchFilterManager.selectedYearId,
        시험범위:
          this.schoolSearchFilterManager.selectedTestTypeId === 'ALL'
            ? '중간기말 전체'
            : this.schoolSearchFilterManager.selectedTestTypeId === 'MIDTERM'
              ? '중간고사'
              : '기말고사',
        추천유형만보기: this.uiStore.filter.recommended,
      }
    } catch (e) {
      commonErrorMessage(e)
    }
  }
}

class UIStore extends DTO {
  openedModalId = EMPTY_NUMBER_ID
  loadingMakeAchievementChip = true
  detailOpen = false
  loadingWorkbookPopover = true
  filter: UIFilterT = createDefaultUIFilter()
  reportOpen = false

  constructor() {
    super()

    makeObservable(this, {
      openedModalId: observable,
      loadingMakeAchievementChip: observable,
      detailOpen: observable,
      filter: observable,
      resetFilter: action,
      makeAchievementChipCleanup: action,
      loadingWorkbookPopover: observable,
      reportOpen: observable,
    })
  }

  resetFilter() {
    this.filter = createDefaultUIFilter()
  }

  makeAchievementChipCleanup = async (fn: () => void) => {
    this.loadingMakeAchievementChip = true
    await fn()
    this.loadingMakeAchievementChip = false
  }
}
